import React, { useState, useEffect } from 'react';
import { HiOutlinePlus, HiOutlineRefresh, HiOutlinePencil, HiOutlineTrash, HiOutlineStatusOnline } from 'react-icons/hi';
import axiosInstance from '../../../api/axiosConfig';
import { SmtpCheckResult } from './SmtpResult';
import './CustomersTab.css';

const UsersTab = ({ canEdit, canDelete, canCreate }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [companyNames, setCompanyNames] = useState([]);
    // Connection test: results per user row, plus the one shown inside the form
    const [rowTests, setRowTests] = useState({});
    const [testingRow, setTestingRow] = useState(null);
    const [formTest, setFormTest] = useState(null);
    const [testingForm, setTestingForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        email_password: '',
        default_email_template: '',
        company_name: ''
    });

    useEffect(() => {
        fetchUsers();
        fetchCompanyNames();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/aging-reports/users');
            if (response.data.success) {
                setUsers(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            alert('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanyNames = async () => {
        try {
            const response = await axiosInstance.get('/api/aging-reports/company-names');
            if (response.data.success && response.data.data?.company_names) {
                setCompanyNames(response.data.data.company_names);
            }
        } catch (error) {
            console.error('Error fetching company names:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Clean formData: remove empty strings and undefined values
            const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
                if (value !== '' && value !== null && value !== undefined) {
                    acc[key] = value;
                }
                return acc;
            }, {});

            if (editingUser) {
                await axiosInstance.put(`/api/aging-reports/users/${editingUser.id}`, cleanedData);
                alert('User updated successfully');
            } else {
                await axiosInstance.post('/api/aging-reports/users', cleanedData);
                alert('User created successfully');
            }
            setShowModal(false);
            if (editingUser) {
                setRowTests((prev) => { const next = { ...prev }; delete next[editingUser.id]; return next; });
            }
            resetForm();
            fetchUsers();
        } catch (error) {
            console.error('Error saving user:', error);
            alert(error.response?.data?.message || 'Failed to save user');
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            name: user.name || '',
            email: user.email || '',
            email_password: '', // Don't populate password for security
            default_email_template: user.default_email_template || '',
            company_name: user.company_name || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        
        try {
            await axiosInstance.delete(`/api/aging-reports/users/${userId}`);
            alert('User deleted successfully');
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Failed to delete user');
        }
    };

    const testErrorResult = (error) => ({
        ok: false,
        title: 'Could not run the test',
        hint: error.response?.data?.message || error.message,
        raw: JSON.stringify(error.response?.data?.error || '')
    });

    // Test the stored password of a saved user
    const handleTestSaved = async (user) => {
        setTestingRow(user.id);
        try {
            const response = await axiosInstance.post(`/api/aging-reports/users/${user.id}/test-connection`);
            setRowTests((prev) => ({ ...prev, [user.id]: response.data.data }));
        } catch (error) {
            setRowTests((prev) => ({ ...prev, [user.id]: testErrorResult(error) }));
        } finally {
            setTestingRow(null);
        }
    };

    // Test what is typed in the form; when editing with a blank password, test the stored one
    const handleTestForm = async () => {
        if (!formData.email_password && !editingUser) {
            setFormTest({ ok: false, title: 'Enter the email password first', hint: '' });
            return;
        }
        setTestingForm(true);
        setFormTest(null);
        try {
            const response = formData.email_password
                ? await axiosInstance.post('/api/aging-reports/users/test-connection', {
                    email: formData.email,
                    email_password: formData.email_password
                })
                : await axiosInstance.post(`/api/aging-reports/users/${editingUser.id}/test-connection`);
            setFormTest(response.data.data);
        } catch (error) {
            setFormTest(testErrorResult(error));
        } finally {
            setTestingForm(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            email_password: '',
            default_email_template: '',
            company_name: ''
        });
        setEditingUser(null);
        setFormTest(null);
    };

    return (
        <div className="customers-tab">
            <div className="tab-header">
                <h2>Email Users Management</h2>
                <div className="tab-actions">
                    <button onClick={fetchUsers} className="btn-icon" title="Refresh">
                        <HiOutlineRefresh />
                    </button>
                    {canCreate && (
                        <button onClick={() => setShowModal(true)} className="btn-primary">
                            <HiOutlinePlus /> Add User
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="loading">Loading users...</div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Company</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="no-data">No users found</td>
                                </tr>
                            ) : (
                                users.map(user => (
                                    <React.Fragment key={user.id}>
                                    <tr>
                                        <td>{user.id}</td>
                                        <td>{user.name}</td>
                                        <td>{user.email}</td>
                                        <td>{user.company_name || '-'}</td>
                                        <td>
                                            <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                                                {user.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    onClick={() => handleTestSaved(user)}
                                                    className="btn-secondary btn-test-connection"
                                                    disabled={testingRow === user.id}
                                                    title="Sign in to the mailbox without sending anything"
                                                >
                                                    <HiOutlineStatusOnline />
                                                    {testingRow === user.id ? 'Testing…' : 'Test connection'}
                                                </button>
                                                {canEdit && (
                                                    <button onClick={() => handleEdit(user)} className="btn-icon" title="Edit">
                                                        <HiOutlinePencil />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button onClick={() => handleDelete(user.id)} className="btn-icon btn-danger" title="Delete">
                                                        <HiOutlineTrash />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {rowTests[user.id] && (
                                        <tr>
                                            <td colSpan="6">
                                                <SmtpCheckResult result={rowTests[user.id]} />
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{editingUser ? 'Edit User' : 'Add User'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Email Password * {editingUser && '(leave blank to keep current)'}</label>
                                <input
                                    type="password"
                                    value={formData.email_password}
                                    onChange={(e) => setFormData({ ...formData, email_password: e.target.value })}
                                    required={!editingUser}
                                    placeholder={editingUser ? 'Leave blank to keep current' : ''}
                                />
                            </div>
                            <div className="form-group">
                                <label>Company (Optional)</label>
                                <select
                                    value={formData.company_name}
                                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                >
                                    <option value="">Select Company</option>
                                    {companyNames.map((company, index) => (
                                        <option key={index} value={typeof company === 'object' ? company.value : company}>
                                            {typeof company === 'object' ? company.name : company}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {editingUser && (
                                <div className="form-group">
                                    <label>Default Email Template (Optional)</label>
                                    <textarea
                                        value={formData.default_email_template}
                                        onChange={(e) => setFormData({ ...formData, default_email_template: e.target.value })}
                                        rows="4"
                                        placeholder="Dear {name}, your aging report is attached..."
                                    />
                                    <small className="template-help">
                                        💡 Use variables: <code>{'{name}'}</code>, <code>{'{ledger_code}'}</code>, <code>{'{company_name}'}</code> | New line: <code>\n</code>
                                    </small>
                                </div>
                            )}
                            <SmtpCheckResult result={formTest} />
                            <div className="modal-actions">
                                <button
                                    type="button"
                                    onClick={handleTestForm}
                                    className="btn-secondary btn-test-connection"
                                    disabled={testingForm || !formData.email}
                                    title={editingUser && !formData.email_password
                                        ? 'Tests the saved password'
                                        : 'Tests the email and password typed above (nothing is saved)'}
                                >
                                    <HiOutlineStatusOnline />
                                    {testingForm ? 'Testing…' : 'Test connection'}
                                </button>
                                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary">
                                    {editingUser ? 'Update' : 'Create'} User
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersTab;
